import { useEffect, useRef, useState } from 'react';

import mapboxgl, { LngLatLike } from 'mapbox-gl';

import { ErrorBanner } from '../../../../components/ErrorBanner/ErrorBanner';
import { MAP_STYLE_URLS } from '../../../../components/Maps/constants/constants';
import { Text } from '../../../../components/Text/Text';
import {
  WEB_GL_DISABLED_ERROR_MESSAGE,
  WEB_GL_DISABLED_WARNING_MESSAGE,
} from '../../../../constants/constants';
import { UNIVERSAL_ENV_VARS } from '../../../../constants/environmentVariables';
import { MapboxExpectedError } from '../../../../helpers/hooks/useNewMap';
import { ICoordinates } from '../../../../interfaces/geo';

import styles from './MapWithMarker.module.css';

mapboxgl.accessToken = UNIVERSAL_ENV_VARS.MAPBOX_TOKEN;

interface MapWithMarkerProps {
  name: string;
  setValue: any;
  coordinates: ICoordinates;
}

export const MapWithMarker = ({ name, setValue, coordinates }: MapWithMarkerProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapError, setMapError] = useState<MapboxExpectedError>();

  useEffect(() => {
    if (!mapContainerRef.current || !coordinates.longitude || !coordinates.latitude) {
      return;
    }

    try {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: MAP_STYLE_URLS.hybrid,
        center: [coordinates.longitude, coordinates.latitude],
        zoom: 10,
      });

      const el = document.createElement('div');
      el.style.width = el.style.height = '36px';
      const img = document.createElement('img');
      img.src = '/images/mapboxStatic/water.png';
      el.appendChild(img);

      const marker = new mapboxgl.Marker({ element: el, draggable: true })
        .setLngLat([coordinates.longitude, coordinates.latitude])
        .addTo(map);

      // Update marker coordinates on dragend event
      const onDragEnd = (): void => {
        const lngLat: LngLatLike = marker.getLngLat();
        setValue(name, { longitude: lngLat.lng, latitude: lngLat.lat });
      };

      marker.on('dragend', onDragEnd);

      return () => {
        map.remove();
        marker.off('dragend', onDragEnd);
      };
    } catch (error) {
      if (error.message.includes(WEB_GL_DISABLED_ERROR_MESSAGE)) {
        setMapError(MapboxExpectedError.WebGLDisabled);
      } else {
        setMapError(MapboxExpectedError.Other);
      }
      return () => {};
    }
  }, [setValue, name, coordinates.longitude, coordinates.latitude]);

  if (mapError === MapboxExpectedError.WebGLDisabled) {
    return <ErrorBanner errorType="webGLDisabled" errors={[WEB_GL_DISABLED_WARNING_MESSAGE]} />;
  }

  if (mapError === MapboxExpectedError.Other) {
    return <Text className="text-error">Error loading map. Please try again later.</Text>;
  }

  return (
    <div ref={mapContainerRef} className={`h-64 w-full rounded-lg ${styles.suggestLocation}`} />
  );
};
